import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { configureAppSync } from "../appsync/config";

export interface UseCustomChatQnaToggleParams {
  eventid: string | number;
  initial?: boolean;
}

export interface UseCustomChatQnaToggleResult {
  enabled: boolean;
  setEnabled: (next: boolean) => Promise<void>;
  toggling: boolean;
  error: string | null;
}

const TOGGLE_CUSTOM_CHAT_QNA = /* GraphQL */ `
  mutation ToggleCustomChatQna(
    $eventid: String!
    $enableCustomChatQna: Boolean!
  ) {
    toggleCustomChatQna(
      eventid: $eventid
      enableCustomChatQna: $enableCustomChatQna
    ) {
      eventid
      enableCustomChatQna
    }
  }
`;

const ON_CUSTOM_CHAT_QNA_TOGGLE = /* GraphQL */ `
  subscription OnCustomChatQnaToggle {
    onCustomChatQnaToggle {
      eventid
      enableCustomChatQna
    }
  }
`;

export function useCustomChatQnaToggle(
  params: UseCustomChatQnaToggleParams,
): UseCustomChatQnaToggleResult {
  const { eventid, initial = false } = params;
  const eventidStr = String(eventid);

  const [enabled, setEnabledState] = useState<boolean>(!!initial);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    configureAppSync();
  }, []);

  const client = useMemo(() => generateClient(), []);

  const initialRef = useRef<boolean>(!!initial);
  useEffect(() => {
    if (initialRef.current !== !!initial) {
      initialRef.current = !!initial;
      setEnabledState(!!initial);
    }
  }, [initial]);

  // Keep sessionStorage in sync with the live toggle so a page refresh
  // mid-webinar reads the current value instead of the stale login snapshot.
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem("eventData");
      if (!cached) return;
      const parsed = JSON.parse(cached);
      if (!!parsed.enableCustomChatQnA !== enabled) {
        parsed.enableCustomChatQnA = enabled;
        sessionStorage.setItem("eventData", JSON.stringify(parsed));
      }
    } catch {
      /* ignore cache write errors */
    }
  }, [enabled]);

  useEffect(() => {
    if (!eventidStr) return;
    let sub: { unsubscribe: () => void } | null = null;
    try {
      const observable = client.graphql({ query: ON_CUSTOM_CHAT_QNA_TOGGLE }) as any;
      sub = observable.subscribe({
        next: ({ data }: any) => {
          const evt = data?.onCustomChatQnaToggle;
          if (!evt) return;
          if (String(evt.eventid) !== eventidStr) return;
          setEnabledState(!!evt.enableCustomChatQna);
          setError(null);
        },
        error: (err: any) => {
          console.warn("[useCustomChatQnaToggle] subscription error", err);
          const msg =
            err?.errors?.[0]?.message ||
            err?.message ||
            (typeof err === "string" ? err : "Subscription error");
          setError(msg);
        },
      });
    } catch (err: any) {
      setError(err?.message || "Failed to open subscription");
    }
    return () => {
      try {
        sub?.unsubscribe();
      } catch {
        /* ignore */
      }
    };
  }, [eventidStr, client]);

  // Don't optimistically set local state — wait for the subscription
  // echo so host + attendees flip on the same tick.
  const setEnabled = useCallback(
    async (next: boolean) => {
      if (!eventidStr) return;
      setToggling(true);
      setError(null);
      try {
        await client.graphql({
          query: TOGGLE_CUSTOM_CHAT_QNA,
          variables: { eventid: eventidStr, enableCustomChatQna: !!next },
        });
      } catch (err: any) {
        const msg =
          err?.errors?.[0]?.message ||
          err?.message ||
          "Failed to toggle custom chat/Q&A";
        setError(msg);
        throw err;
      } finally {
        setToggling(false);
      }
    },
    [client, eventidStr],
  );

  return { enabled, setEnabled, toggling, error };
}
